import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useAuthStore } from "../../store/auth.store";

// Re-export shadcn components for backward compatibility
export { Button } from "../ui/button";
export { Input, InputField } from "../ui/input";
export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardDescription,
  CardContent,
} from "../ui/card";
export { Badge } from "../ui/badge";
export {
  Modal,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
export { Label } from "../ui/label";
export {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
export { Textarea } from "../ui/textarea";
export { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
export { Checkbox } from "../ui/checkbox";
export { Switch } from "../ui/switch";
export { Separator } from "../ui/separator";
export { Progress } from "../ui/progress";
export { Skeleton } from "../ui/skeleton";
export {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "../ui/tooltip";
export { Avatar, AvatarImage, AvatarFallback } from "../ui/avatar";
export { ScrollArea, ScrollBar } from "../ui/scroll-area";

// Loading Screen component
export function LoadingScreen() {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
        <p className="mt-4 text-muted-foreground">{t("common.loading")}</p>
      </div>
    </div>
  );
}

// Protected Route component
export function ProtectedRoute({ children }) {
  const { isAuthenticated } = useAuthStore();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
}

// Role-based guards
export {
  RoleGuard,
  AdminGuard,
  OwnerGuard,
  AdminOrOwnerGuard,
  OwnerFeatureGuard,
} from "./RoleGuard";

// Feature-based guards
export {
  UpgradePrompt,
  FeatureGate,
  FeatureBadge,
  LockedOverlay,
} from "./FeatureGate";

// Empty State component
export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  className,
}) {
  return (
    <div className={cn("text-center py-12", className)}>
      {Icon && (
        <div className="mx-auto w-12 h-12 text-muted-foreground mb-4">
          <Icon className="w-full h-full" />
        </div>
      )}
      <h3 className="text-lg font-medium text-foreground mb-1">{title}</h3>
      <p className="text-muted-foreground mb-4">{description}</p>
      {action}
    </div>
  );
}

// Re-export default for convenience
export default LoadingScreen;
